<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb name="Billing"/>
    <b-row>
      <b-col cols="12">
        <admin-panel title="Pricing Plans" sub-title="All Company's Paid Pricing Plans">
          <template v-slot:header-right>
            <div class="mt-n2 d-flex flex-wrap admin-order-filters">
              <div class="mt-1 mr-2">
                <div>Company</div>
                <admin-companies-autocomplete v-model="billing_filters.company" class="mt-1"/>
              </div>
              <div class="mt-1 mr-2">
                <div>Pricing Plan</div>
                <b-form-select
                    class="w-auto mt-1 orders-filter-select"
                    v-model="billing_filters.pricing_plan"
                    :options="paidPricingPlanOptions"
                />
              </div>
              <div class="mt-1">
                <div>Status</div>
                <b-form-select
                    class="w-auto mt-1 orders-filter-select"
                    v-model="billing_filters.status"
                    :options="billing_status_options"
                />
              </div>
            </div>
          </template>

          <admin-billing-table
              :billing="billing"
              @retry-payment="retryPricingPlanPayment"
              @next-charge-change="updatePricingPlanPaymentNextChargeDate"
          />
        </admin-panel>
        <admin-panel title="Charges" sub-title="All Payments Made" with-footer>
          <template v-slot:header-right>
            <div class="mt-n2 d-flex flex-wrap admin-order-filters">
              <div class="mt-1 mr-2">
                <div>Company</div>
                <admin-companies-autocomplete v-model="charges_filters.company" class="mt-1"/>
              </div>
              <div class="mt-1 mr-2">
                <div>Payment Type</div>
                <b-form-select
                    class="w-auto mt-1 orders-filter-select"
                    v-model="charges_filters.type"
                    :options="charge_type_options"
                    @change="loadCharges(1)"
                />
              </div>
              <div class="mt-1">
                <div>Show</div>
                <b-form-select
                    class="w-auto mt-1 orders-filter-select"
                    v-model="charges_per_page"
                    :options="per_page_select_options"
                    @change="loadCharges(1)"
                />
              </div>
            </div>
          </template>

          <b-overlay :show="charges_loading">
            <admin-charges-table :charges="charges"/>
          </b-overlay>

          <template v-slot:footer>
            <b-pagination
                v-if="total_charges_in_pages > 0"
                v-model="charges_page"
                :total-rows="total_charges_in_pages"
                :per-page="charges_per_page"
                @change="loadCharges"
                align="center"
                class="admin-pagination mb-0"
            />
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPanel from "../components/AdminPanel";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminPreloader from "../components/AdminPreloader";
import AdminBillingTable from "../components/AdminBillingTable.vue";
import AdminChargesTable from "../components/AdminChargesTable.vue";
import AdminCompaniesAutocomplete from "../components/AdminCompaniesAutocomplete.vue";

export default {
  name: "AdminBilling",
  components: {
    AdminCompaniesAutocomplete,
    AdminChargesTable, AdminBillingTable, AdminPreloader, AdminBreadcrumb, AdminPanel},
  data() {
    return {
      loading: true,
      billing_rows: [],
      billing_filters: {
        company: null,
        pricing_plan: null,
        status: ''
      },
      paid_pricing_plans: [],
      billing_status_options: [
        {
          value: "",
          text: "All Statuses"
        },
        {
          value: "active",
          text: "Active"
        },
        {
          value: "trial",
          text: "Trial"
        },
        {
          value: "payment_failed",
          text: "Payment Failed"
        },
        {
          value: "stopped",
          text: "Stopped"
        },
        {
          value: "finished",
          text: "Finished"
        },
      ],
      charges_loading: true,
      charges: [],
      charges_page: 1,
      charges_per_page: 20,
      total_charges_in_pages: 0,
      charges_filters: {
        company: null,
        type: ''
      },
      charge_type_options: [
        {
          value: "",
          text: "All Types"
        },
        {
          value: "order_fee",
          text: "Order Fee Payments"
        },
        {
          value: "pricing_plan",
          text: "Pricing Plan Payments"
        },
      ],
      per_page_select_options: [
        {
          value: 10,
          text: "10",
        },
        {
          value: 20,
          text: "20",
        },
        {
          value: 50,
          text: "50",
        },
      ],
    }
  },
  methods: {
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    loadBilling() {
      this.$api.get(API_ROUTES.ADMIN_BILLING).then(({data}) => {
        this.billing_rows = data.billing;
      }).catch(({response: {data}}) => {
        this.$bvToast.toast('Failed to load billing', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      })
    },
    loadCharges(page = 1) {
      this.charges_loading = true;
      this.$api.get(`${API_ROUTES.ADMIN_BILLING}/charges`, {
        params: {
          per_page: this.charges_per_page,
          page: page,
          company: this.charges_filters.company ? this.charges_filters.company.id : null,
          type: this.charges_filters.type.length > 0 ? this.charges_filters.type : null
        }
      }).then(({data}) => {
        this.charges = data.charges;
        this.total_charges_in_pages = data.total;
      }).catch(({response: {data}}) => {

      }).finally(() => {
        if (page !== this.charges_page) {
          this.charges_page = page;
        }
        this.charges_loading = false;
      })
    },
    loadPaidPricingPlans() {
      this.$api.get(API_ROUTES.ADMIN_PRICING_PLANS, {
        params: {
          is_free: 0
        }
      }).then(({data}) => {
        this.paid_pricing_plans = data.pricing_plans;
      });
    },
    retryPricingPlanPayment(data) {
      if (!this.checkPermission('canRetryBillingPayments')) {
        return;
      }
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_BILLING}/retry-payment`, {
        company: data.company.id,
        pricing_plan: data.pricing_plan.id
      }).then(({data}) => {
        this.billing_rows = data.billing;
        this.$bvToast.toast('Payment succeeded', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
        this.charges_loading = true;
        this.loadCharges();
      });
    },
    updatePricingPlanPaymentNextChargeDate(data) {
      if (!this.checkPermission('canUpdateBilling')) {
        return;
      }
      this.loading = true;
      this.$api.post(`${API_ROUTES.ADMIN_BILLING}/update-next-charge-date`, {
        company: data.company.id,
        pricing_plan: data.pricing_plan.id,
        charge_date: data.date
      }).then(({data}) => {
        this.billing_rows = data.billing;
        this.$bvToast.toast('Next Charge Date Updated', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response: {data}}) => {
        this.$bvToast.toast(data.message ?? 'Failed to process operation', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  computed: {
    canUpdateBilling() {
      return this.$store.getters['auth/hasAdminPermissions']('billing_update');
    },
    canRetryBillingPayments() {
      return this.$store.getters['auth/hasAdminPermissions']('billing_retry');
    },
    billing() {
      let billing = [...this.billing_rows];
      if (this.billing_filters.company) {
        billing = billing.filter(b => b.company.id === this.billing_filters.company.id);
      }
      if (this.billing_filters.pricing_plan) {
        billing = billing.filter(b => b.pricing_plan && b.pricing_plan.id === this.billing_filters.pricing_plan);
      }
      if (this.billing_filters.status.length > 0) {
        switch (this.billing_filters.status) {
          case 'finished':
            billing = billing.filter(b => b.is_finished);
            break;
          case 'stopped':
            billing = billing.filter(b => !b.is_finished && !!b.finishes_at);
            break;
          case 'payment_failed':
            billing = billing.filter(b => !b.is_finished && !b.finishes_at && b.last_paid && !!b.last_paid.date_failed);
            break;
          case 'active':
            billing = billing.filter(b => !b.is_finished && !b.finishes_at && b.last_paid && !b.last_paid.date_failed);
            break;
          case 'trial':
            billing = billing.filter(b => !b.is_finished && !b.finishes_at && !b.last_paid);
            break;
        }
      }

      return billing;
    },
    paidPricingPlanOptions() {
      return [
        {
          value: null,
          text: "All Plans"
        },
        ...this.paid_pricing_plans.map(pp => ({
          value: pp.id,
          text: `#${pp.id} ${pp.name}`
        }))
      ];
    }
  },
  watch: {
    "charges_filters.company": function () {
      this.loadCharges();
    }
  },
  created() {
    this.loadBilling();
    this.loadCharges();
    this.loadPaidPricingPlans();
  }
}
</script>
